import {
  Component,
  OnInit,
  forwardRef,
  Output,
  EventEmitter,
} from "@angular/core";
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from "@angular/forms";
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from "@angular/animations";

import { MOCK_ORGANS_DATA } from "./test-data";

export const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => OrDevComponent),
  multi: true,
};

@Component({
  selector: "app-or-dev",
  templateUrl: "./or-dev.component.html",
  styleUrls: ["./or-dev.component.scss"],
  animations: [
    trigger("transformMenu", [
      state(
        "void",
        style({
          opacity: 0,
          transform: "scale(0.8)",
        })
      ),
      transition(
        "void => enter",
        animate(
          "120ms cubic-bezier(0, 0, 0.2, 1)",
          style({
            opacity: 1,
            transform: "scale(1)",
          })
        )
      ),
      transition(
        "* => void",
        animate("100ms 25ms linear", style({ opacity: 0 }))
      ),
    ]),
  ],
  providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR],
})
export class OrDevComponent implements OnInit, ControlValueAccessor {
  @Output("menu-event") menuEvent = new EventEmitter<any[]>();
  public allMenuOptions: any[] = [];
  public _filteredMenuOptions: any[] = [];
  public selectedOptions: any[] = [];
  public menuIsOpen = false;
  public filterText = "";
  public displayedValue = "בחר מתוך הרשימה";
  public disabled = false;
  public showFilterBox = true;
  public appearance = "";
  public length = 0;
  // 1. הגדרת משתנה המחלקה מחוץ לכל פונקציה
  private optionsMap = new Map<string, any>();

  constructor() {}

  public getOrganTypeName(organType: "circle" | "county" | "nest"): string {
    return MOCK_ORGANS_DATA.organTypeToText(organType);
  }

  ngOnInit() {
    // 2. קריאה לפונקציה בלבד
    this.loadMockData();
  }
  toggleMenu(some: any) {}

  public clickedOption($event: MouseEvent, key: string) {
    $event.stopPropagation();
    $event.stopImmediatePropagation();

    const optionClicked = this._filteredMenuOptions.find((o) => o.key === key);
    // tslint:disable-next-line:curly
    if (!optionClicked) return;

    if (this.selectedOptions.some((o) => o.key === optionClicked.key)) {
      this.selectedOptions = this.selectedOptions.filter(
        (o) => o.key !== optionClicked.key
      );
    } else {
      this.selectedOptions = [...this.selectedOptions, optionClicked];
    }

    this.selectedOptions = this.applySelectionRules(this.selectedOptions);

    this.updateFilteredMenuOptions();
    this.menuEvent.emit(this.selectedOptions);
    this.updateDisplayedValue();
    this.onChange(this.selectedOptions);
  }

  public isOptionSelected(key: string): boolean {
    return this.selectedOptions.some((o) => o.key === key);
  }

  public focusOut($event: any) {}

  // ... (שאר המתודות שלך כמו getAncestors ו-applySelectionRules נשארות כפי שהן)

  public menuKeyDown($event: any) {}

  public filterChanged($event: any) {
    this.filterText = $event.target.value;
    this.updateFilteredMenuOptions();
  }

  public clearFilter() {
    this.filterText = "";
    this.updateFilteredMenuOptions();
  }

  public clearSelection($event: any) {
    $event.stopPropagation();
    this.selectedOptions = [];

    this.updateDisplayedValue();
    this.updateFilteredMenuOptions();

    this.onChange(this.selectedOptions);
    this.onTouched();
  }

  writeValue(value: any): void {
    this.selectedOptions = value || [];
    this.updateDisplayedValue();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public updateFilteredMenuOptions() {
    const text = this.filterText.toLowerCase().trim();
    const LIMIT = 10;
    const selectedKeys = new Set(this.selectedOptions.map((o) => o.key));

    const isDescendantOfSelected = (item: any) => {
      let parentId = item.customValue.parentId;
      while (parentId) {
        if (selectedKeys.has(parentId)) return true;
        const parent = this.allMenuOptions.find((o) => o.key === parentId);
        parentId = parent ? parent.customValue.parentId : null;
      }
      return false;
    };

    let baseOptions = text.length
      ? this.allMenuOptions.filter((o) => o.text.toLowerCase().includes(text))
      : [...this.allMenuOptions];

    const filteredOptions = baseOptions.filter(
      (o) => !isDescendantOfSelected(o)
    );

    const selected = filteredOptions.filter((o) => selectedKeys.has(o.key));
    const unselected = filteredOptions.filter((o) => !selectedKeys.has(o.key));

    // לוגיקת תצוגה
    if (text.length > 0) {
      this._filteredMenuOptions = [...selected, ...unselected];
    } else {
      if (selected.length >= LIMIT) {
        this._filteredMenuOptions = selected.slice(0, LIMIT);
      } else {
        const remainingSpace = LIMIT - selected.length;
        this._filteredMenuOptions = [
          ...selected,
          ...unselected.slice(0, remainingSpace),
        ];
      }
    }
  }

  public updateDisplayedValue() {
    this.displayedValue =
      this.selectedOptions.length > 0
        ? this.selectedOptions.map((o) => o.text).join(", ")
        : "בחר מתוך הרשימה";
  }

  private onChange = (val: any) => {};

  private onTouched = () => {};

  private loadMockData() {
    const circles = MOCK_ORGANS_DATA.circles.map((circle) => ({
      key: circle.code,
      text: circle.name,
      customValue: { type: "circle", parentId: null },
    }));
    // ... (השאר נשאר אותו דבר)
    // TODO: Or - this line throw an error and was commented out
    // this.allMenuOptions = [...circles, ...counties, ...nests];

    this.optionsMap.clear();
    this.allMenuOptions.forEach((option) => {
      this.optionsMap.set(option.key, option);
    });

    this.updateFilteredMenuOptions();
  }

  // פונקציית עזר רקורסיבית למציאת כל האבות - ללא WHILE
  private getAncestors(item: any): any[] {
    const parent = this.optionsMap.get(item.customValue.parentId);
    // tslint:disable-next-line:curly
    if (!parent) return [];
    return [parent, ...this.getAncestors(parent)];
  }

  private applySelectionRules(selected: any[]): any[] {
    const selectedKeys = new Set(selected.map((o) => o.key));

    // נשתמש ב-filter ו-some כפי שביקשת
    return selected.filter((item) => {
      const ancestorIds = this.getAncestors(item).map((a) => a.key);
      // תשאיר רק אם אף אחד מהאבות לא נמצא ב-selectedKeys
      return !ancestorIds.some((id) => selectedKeys.has(id));
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/or-dev/or-dev.component.ts